import { Button } from "@nimit9/signet-ui/components/button"
import { Input } from "@nimit9/signet-ui/components/input"
import type { ParsedStatement } from "@paisa/types"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { api } from "@/lib/api"
import { accountTypeLabel } from "./helpers"

/**
 * Add the statement's account without leaving the import (UAT J10-03, J4-03):
 * bank, type and last 4 come from the parsed statement; the owner is you.
 */
export function CreateAccountInline({
  parsed,
  onCreated,
}: {
  parsed: Pick<ParsedStatement, "bank" | "accountType" | "accountNumber">
  onCreated: (id: string) => void
}) {
  const qc = useQueryClient()
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<{ data?: { owner?: string | null } }>("/profile"),
  })
  const bank = parsed.bank.toUpperCase()
  const [last4, setLast4] = useState(parsed.accountNumber?.slice(-4) ?? "")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const name = `${bank} ${accountTypeLabel(parsed.accountType)}`
  const valid = /^\d{4}$/.test(last4)

  async function create() {
    setSaving(true)
    setError(null)
    try {
      const res = await api.post<{ data: { id: string } }>("/accounts", {
        name,
        bankName: bank,
        type: parsed.accountType,
        last4,
        owner: profile?.data?.owner ?? "joint",
      })
      await qc.invalidateQueries({ queryKey: ["accounts"] })
      onCreated(res.data.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't add the account")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="create-account-inline" className="flex flex-wrap items-end gap-2">
      <div className="flex min-w-0 flex-col gap-1">
        <label htmlFor="new-account-last4" className="text-caption text-text-muted">
          Last 4 digits of {name}
        </label>
        <Input
          id="new-account-last4"
          inputMode="numeric"
          maxLength={4}
          value={last4}
          onChange={(e) => setLast4(e.target.value.replace(/\D/g, ""))}
          className="w-28"
        />
      </div>
      <Button size="touch" className="md:h-9" disabled={!valid || saving} onClick={create}>
        {saving ? "Adding…" : `Add ${name} ••${last4 || "····"}`}
      </Button>
      {error && (
        <p role="alert" className="w-full text-caption text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
